/*
 *   File : pan-details.js
 *   Author URI : https://evoqins.com
 *   Description :  Component will allow the customer to submit PAN details.
 *   Integrations : null
 *   Version : v1.1
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { parse, format } from 'date-fns';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import { CustomTextInput, DatePicker } from "../../../Components/FormElements"
import { GradientButton } from "../../../Components/Cta";
import APIService from "../../../Services/api-service";
import { _getKycProfile } from "../../../Helper/api";
import CustomFilePicker from "../../../Components/FormElements/file-upload";
import { FilePreview } from "../../../Components/Cards";
import { _uploadDocumentToS3 } from "../../../Services/aws-service";
import { _isFileTypeAllowed } from "../../../Helper/helper-function";
import Store from "../../../Store";
import { Modal } from "bootstrap";
import AcceptPanConsent from "../../../Components/Modal/pan-consent";

const PANDetailsPage = (props) => {

    const PAN_DATA = useSelector(state => state.MT_ADMIN_STORE.KYC_DATA.pan_details);
    const KIRA_INFO = useSelector(state => state.MT_ADMIN_STORE.KYC_DATA.kra_info);
    const location = useLocation();
    const PROGRESS_DATA = useSelector(state => state.MT_ADMIN_STORE.KYC_DATA);
    console.log('PROGRESS_DATA', PROGRESS_DATA);


    const [panNumber, setPanNumber] = useState(PAN_DATA.pan ? PAN_DATA.pan : "");
    const [panNumberError, setPanNumberError] = useState("");
    const [dob, setDOB] = useState(null);
    const [dobError, setDobError] = useState("");
    const [loader, setLoader] = useState(false);
    const [name, setName] = useState(KIRA_INFO.name ? KIRA_INFO.name : "");
    const [nameError, setNameError] = useState("");
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [uploadedPan, setUploadedPan] = useState(null);
    const [uploadedPanError, setUploadedPanError] = useState("");
    const [fileLoader, setFileLoader] = useState(false);
    const [panUrl, setPanUrl] = useState("");
    const [poi, setPoi] = useState("");
    const [poiError, setPoiError] = useState("");
    const [consentRequired, setConsentRequired] = useState(false);
    const [apiData, setApiData] = useState({});

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const _handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', _handleResize);

        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener('resize', _handleResize);
        };
    }, []);

    // max date that can be selected for dob (current date - 18 years)
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 18);

    useEffect(() => {
        if (PAN_DATA.date_of_birth !== null) {
            const parsed_date = parse(PAN_DATA.date_of_birth, 'dd-MM-yyyy', new Date());
            const formatted_date = format(parsed_date, 'dd/MM/yyyy');
            setDOB(formatted_date);
        }
    }, []);

    useEffect(() => {
        if (consentRequired === true) {
            const modal = new Modal(document.getElementById("pan-consent"), {});
            modal.show();
        }
    }, [consentRequired])

    useEffect(() => {
        if (PAN_DATA.place_of_incorporation !== null) {
            setPoi(PAN_DATA.place_of_incorporation);
        }
    }, [PAN_DATA])


    useEffect(() => {
        if (PROGRESS_DATA.huf_pan_url !== null) {
            setPanUrl(PROGRESS_DATA.huf_pan_url)
            const decodedURL = decodeURIComponent(decodeURIComponent(PROGRESS_DATA.huf_pan_url));

            // Extract file name from URL

            const fileName = decodedURL.split('?')[0].split("/").pop();
            setUploadedPan(fileName)

        }
    }, [PROGRESS_DATA])


    function _handleDatePickerFocus() {
        let element = document.getElementById('footer-div');
        setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' })
        }, 200);

    }
    function _handleProceed() {
        window.location.href = "/user-summary"
    }

    // handler for input fields
    const _handleInputFields = (type, value, file) => {
        switch (type) {
            case 1:
                setPanNumber(value.toUpperCase());
                setPanNumberError("");
                break;
            case 2:
                setDOB(value);
                setDobError("");
                break;
            case 3:
                // eslint-disable-next-line
                var file_formatted = value.replace(/^.*[\\\/]/, '').split('/');
                file_formatted = file_formatted[0].replace(/[\(\)\s]/g, '');
                const parts = file_formatted.split('.');
                if (parts.length > 1) {
                    const extension = '.' + parts.pop();
                    if (_isFileTypeAllowed(extension)) {
                        // checking if file size is greater than 5mb
                        const file_size = 5 * 1024 * 1024
                        if (file.size < file_size) {
                            _fileUpload(file_formatted, file);
                        } else {
                            setUploadedPanError("File size should be less than 5mb");
                        }
                    }
                    else {
                        setUploadedPanError("File type should be .pdf, .jpeg, .jpg, .png");
                    }
                }
                break
            case 4:
                setName(value);
                setNameError("");
                break;
        }
    }

    //API :: file upload api
    const _fileUpload = (key, file) => {
        setUploadedPanError("");
        setFileLoader(true);
        let url = "general/file-upload";
        let body = {
            "key": `support/${key}`
        }

        APIService(true, url, body).then((response) => {
            if (response.status_code === 200) {
                setFileLoader(true);

                // s3 helper
                _uploadDocumentToS3(response.data, key, file).then((result) => {
                    setPanUrl(response.data.view_info); // file got uploaded successfully
                    setFileLoader(false);
                    setUploadedPan(key);
                }).catch(error => {
                    toast.dismiss();
                    toast.error(error, {
                        type: "error",
                    });
                    setFileLoader(false);
                });

            } else {
                setFileLoader(false);
                toast.dismiss();
                toast.error(response, {
                    type: "error",
                });
            }
        })
    }

    const _validate = () => {
        let valid = true;

        if (dob === null) {
            setDobError("Enter date of birth");
            valid = false;
        }
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)) {
            console.log('panNumber', panNumber);
            setPanNumberError("Enter valid pan number");
            valid = false;
        }
        if (name === "") {
            setNameError("Please enter valid name");
            valid = false;
        }
        if (uploadedPan === null && PROGRESS_DATA.onboarding_sub_type === "HUF") {
            setUploadedPanError("Please upload PAN");
            valid = false;
        }
        if (poi === "" && PROGRESS_DATA.onboarding_sub_type === "HUF") {
            setPoiError("Enter Place of Incorporation");
            valid = false;
        }
        if (valid === true) {
            setLoader(true);
            _saveDetails();
        }
    };



    // API - save pan number and dob for kyc
    const _saveDetails = async () => {
        const formatted_date = format(parse(dob, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
        let url = 'kyc/check';
        let payload = JSON.stringify({
            pan: panNumber,
            dob: formatted_date,
            name_on_pan: name,
            place_of_incorporation: poi !== "" ? poi : null,
            customer_id: location.state.user_id,
            huf_pan_url: uploadedPan === null ? null : panUrl
        });

        try {
            const response = await APIService(true, url, payload, true);
            toast.dismiss();
            if (response.status_code === 200) {

                Store.dispatch({
                    type: 'GET_KYC_DATA',
                    payload: response.kyc_data
                });

                props.updateProgress(3, response.kyc_data.onboarding_sub_type_id === 9 ? 'Individual' : props.text, props.is_manual); //props.text
                toast.success("PAN details submitted successfully", {
                    type: "success",
                });
            } else {
                setConsentRequired(response.consent_required);
                setApiData(response);
                toast.dismiss();
                if (response.consent_required === false) {
                    toast.error(response.message, {
                        type: "error",
                    });
                }
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error.message, {
                type: "error",
            });
        }
        finally {
            setLoader(false);
        }
    };


    return (
        <div>
            <p className="color-black e-poppins e-poppins-semi-bold e-font-24-sm-18 mb-2">Enter PAN details</p>
            <p className="color-outer-space e-poppins-regular e-font-16 e-font-sm-16-14 line-height-24px mb-0">Submit PAN card details. This is a regulatory requirement.</p>
            <div className="row margin-34px-top mb-5">
                <div className="col-md-5 padding-62px-right-mob padding-5px-right-mob">
                    <CustomTextInput label="PAN number"
                        postfix="*"
                        pan={true}
                        value={panNumber}
                        max={10}
                        uppercase={true}
                        inputClass="text-uppercase"
                        error={panNumberError}
                        className="margin-42px-bottom "
                        onEnter={_validate}
                        valueChange={(value) => _handleInputFields(1, value)} />

                    <CustomTextInput label="Enter name as per PAN"
                        postfix="*"
                        value={name}
                        error={nameError}
                        className="margin-42px-bottom "
                        onEnter={_validate}
                        valueChange={(value) => _handleInputFields(4, value)} />

                    <DatePicker label={PROGRESS_DATA.onboarding_sub_type == "HUF" ? "Enter DOI" : "Enter date of birth as per PAN"}
                        error={dobError}
                        value={dob}
                        noHeight={true}
                        isAdult={true}
                        maxDate={PROGRESS_DATA.onboarding_sub_type == "HUF" ? new Date() : maxDate}
                        postFix="*"
                        dob={(value) => _handleInputFields(2, value)}
                    // openDateModal={_handleDatePickerFocus}
                    />

                    {PROGRESS_DATA.onboarding_sub_type === "HUF" && <CustomTextInput label="Place of Incorporation"
                        value={poi}
                        postfix="*"
                        error={poiError}
                        className="margin-42px-top"
                        onEnter={_validate}
                        valueChange={(value) => {
                            setPoi(value);
                            setPoiError("");
                        }} />}

                    {
                        PROGRESS_DATA.onboarding_sub_type === "HUF" && <div className="mt-4">
                            <h6 className="color-black e-font-18 e-poppins-medium mb-2">
                                Upload {props.text} PAN
                            </h6>
                            <p className="color-outer-space e-poppins-regular e-font-16 line-height-24px mb-0">Securely upload your {props.text} PAN to proceed without delays.</p>
                            <div className="col-12">
                                <p className="color-black e-font-12 e-poppins-medium mb-2 mt-3">Upload PAN <span className='color-red'>*</span> </p>



                                <CustomFilePicker accept=".png, .pdf, .jpeg "
                                    className="e-select-file"
                                    error={uploadedPanError}
                                    loading={fileLoader}
                                    type={1}
                                    onSelectFile={(path, file) => {
                                        _handleInputFields(3, path, file);
                                    }} />
                                {
                                    uploadedPan === null ?
                                        null
                                        :
                                        <div className="col-12 pt-3">
                                            <FilePreview fileName={uploadedPan}
                                                showBinIcon={true}
                                                hidePreviewIcon={true}
                                                clearFile={() => {
                                                    setUploadedPan(null);
                                                    setPanUrl("");
                                                }} />
                                        </div>
                                }

                            </div>
                        </div>
                    }

                    <GradientButton label="Continue"
                        loading={loader}
                        className="px-3 padding-12px-tb e-font-16 margin-40px-top mob-w-100"
                        onPress={_validate} />
                </div>
                <div className="col-md-5 padding-5px-right-mob mt-lg-0 mt-md-0 mt-4 d-flex justify-content-end align-items-start">
                    <img src={require("../../../Assets/Images/kyc/pan-image.png")}
                        alt="Pan"
                        width={screenWidth > 576 ? 414 : 380}
                        draggable={false} />
                </div>
                {
                    consentRequired === true &&
                    <AcceptPanConsent accept={_handleProceed}
                        id={location.state.user_id}
                        apiData={apiData}
                        close={() => setConsentRequired(false)} />
                }
            </div>
            <div id="footer-div"></div>
        </div>
    )
}

export default PANDetailsPage
